import React from 'react';
import { createPortal } from 'react-dom';
import { Typography } from '@mui/material';

import StatementTable from './StatementTable';

// ----------------------------------------------------------------------
// GivingStatementPrint — the printable copy of a member's giving
// statement (letterhead + full transactions table), rendered through a
// React portal directly onto `mountNode` (document.body by default).
//
// Why a portal: the on-screen statement lives inside a shared
// EditorialSection, whose wrapping Box sets `position: relative` and
// `overflow: hidden` (used for parallax effects elsewhere on the site).
// The classic print-isolation trick — hide everything, then
// `position: absolute; top:0; left:0` the target — anchors to that
// nearest positioned ancestor instead of the page, and gets clipped by
// its `overflow: hidden` once the absolutely-positioned content no
// longer contributes to the section's flow height. That silently
// truncated statements for any member with real giving history.
//
// Mounting this copy as a direct child of <body> sidesteps the whole
// ancestor chain — there is nothing between it and the page to clip or
// mis-anchor it, regardless of how many years/rows the statement has.
// See src/styles/App.css's `@media print` rules for `#giving-statement-print`.
// ----------------------------------------------------------------------
export default function GivingStatementPrint({
  statement,
  statementYear,
  transactions,
  donationTotal,
  paymentTotal,
  mountNode,
}) {
  if (!statement) return null;

  const target = mountNode || (typeof document !== 'undefined' ? document.body : null);
  if (!target) return null;

  return createPortal(
    <div id="giving-statement-print">
      <Typography variant="h5" fontWeight={700}>
        Kehilat Bnei Torah
      </Typography>
      <Typography variant="body2">9017 W. Pico Blvd., Los Angeles</Typography>
      <Typography variant="h6" sx={{ mt: 2 }}>
        Giving Statement — {statement.memberName}
      </Typography>
      <Typography variant="body2">
        {statementYear === 'all' ? 'All years' : statementYear}
        {'  ·  '}Generated {new Date().toLocaleDateString()}
      </Typography>

      <StatementTable
        transactions={transactions}
        statementYear={statementYear}
        donationTotal={donationTotal}
        paymentTotal={paymentTotal}
      />
    </div>,
    target
  );
}
